import axios from 'axios'
import request from '@/utils/request'
import { installAppVersionGuard } from '@/utils/appVersion'
import { installAuthGuard } from '@/utils/authSession'
import { API_BASE_URL, TOKEN_KEY } from '@/config/constants'
import {
  createPermissionBlockedError,
  getBlockedPermissionRule,
  getEmptyPermissionResponse
} from '@/config/permissionGuards'

const scriptRequest = installAppVersionGuard(axios.create())
installAuthGuard(scriptRequest)

export function recordScriptOpened(scriptId, options = {}) {
  return request({
    ...options,
    url: '/api/scripts/' + encodeURIComponent(scriptId) + '/opened',
    method: 'post',
    silent: true
  })
}

export function getImageScriptCoverCandidates(scriptId, cursor, options = {}) {
  return request({
    ...options,
    url: '/api/scripts/' + encodeURIComponent(scriptId) + '/image-cover-candidates',
    method: 'get',
    params: cursor || {}
  })
}

export function initializeImageScriptCover(scriptId, taskId, imageUrl, options = {}) {
  return request({
    ...options,
    url: '/api/scripts/' + encodeURIComponent(scriptId) + '/image-cover',
    method: 'put',
    data: { taskId, imageUrl }
  })
}

export async function getImageScriptSummaries(scriptIds) {
  const token = localStorage.getItem(TOKEN_KEY)
  const response = await scriptRequest.get(`${API_BASE_URL.replace(/\/$/, '')}/api/scripts/image-summaries`, {
    params: { scriptIds: scriptIds.join(',') },
    headers: token ? { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` } : {},
    timeout: 15000,
    silent: true
  })
  return response.data
}

/**
 * 获取剧本列表
 * 说明：部分后端直接返回数组，为兼容性使用原生 axios 请求并统一返回 { code: 200, data: [...] } 或直接返回后端标准结构
 * @param {Object} params - 可选查询参数
 * @returns {Promise}
 */
export async function getScripts(params = {}) {
  const config = {
    url: '/api/scripts/mine',
    method: 'get'
  }
  const blockedRule = getBlockedPermissionRule(config)
  if (blockedRule) {
    const emptyResponse = getEmptyPermissionResponse(blockedRule, config)
    if (emptyResponse) return emptyResponse
    return Promise.reject(createPermissionBlockedError(blockedRule.permission))
  }

  const headers = {}
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) {
    headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`
  }

  const res = await scriptRequest.get(`${API_BASE_URL.replace(/\/$/, '')}/api/scripts/mine`, {
    params,
    headers,
    timeout: 30000
  })

  // 如果后端直接返回数组，包装成 { code: 200, data: [...] }
  if (Array.isArray(res.data)) {
    return { code: 200, data: res.data }
  }

  // 如果后端已经是统一格式（如 { code: 200, data: [...] }），直接返回
  return res.data
}
